import { stateful } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html } from 'lit';

const HeaderBar = () => html`
  <div class="navbar rounded-box sticky top-0 z-20 flex-wrap gap-2 p-4 justify-between">
    <div class="">
      <button class="btn btn-outline" @click=${() => window.history.back()}>
        <span class="icon-[material-symbols--chevron-left] text-xl" aria-hidden="true"></span>
        Zurück
      </button>
    </div>
    <h1 class="text-center text-2xl font-semibold">Datenschutzerklärung</h1>
    <div class="">
      <button class="btn btn-outline" @click=${() => goTo('/')}>
        Login/Dashboard
        <span class="icon-[material-symbols--home] text-xl" aria-hidden="true"></span>
      </button>
    </div>
  </div>
`;

const Intro = () => html`
  <div class="card">
    <div class="card-body max-w-none">
      <p class="leading-relaxed">
        Vielen Dank für Ihren Besuch auf unserer Website / Portal. Wir freuen uns, dass Sie sich für die
        Schulgesellschaft Sankt Martinus (gGmbH) und Ihre Services interessieren. Der Schutz persönlicher Daten von
        Sorgeberechtigten, Schülern**, Bewerbern, Mitarbeitern und allen mit uns verbundenen Dritten ist uns besonders
        wichtig.
        <br />
        Bei der Verarbeitung personenbezogener Daten beachten wir die einschlägigen gesetzlichen Bestimmungen zum
        Datenschutz und zur Datensicherheit sowie unsere internen Datenschutzbestimmungen (kirchliches
        Datenschutzgesetz).
        <br />
        Diese Datenschutzerklärung enthält Informationen, wie wir mit personenbezogenen Daten umgehen, die auf unseren
        Webseiten erhoben werden oder die Sie uns übermitteln, und erläutert den Umgang mit Ihren persönlichen Daten.
        <br />
        Zur besseren Übersichtlichkeit ist diese Datenschutzerklärung in drei Teile gegliedert: Teil A enthält
        allgemeine Informationen. Landes- und unternehmensspezifische Besonderheiten zum Thema Datenschutz werden in
        Teil B erläutert. Teil C gibt Einblicke in technisch relevante Informationen.
      </p>
    </div>
  </div>
`;

const TeilA = () => html`
  <div class="card">
    <div class="card-body flex flex-col gap-8 prose prose-neutral max-w-none">
      <h2>Teil A - Allgemeine Informationen</h2>

      <div class="flex flex-col gap-4">
        <h3>1. Welchen Zweck hat die Erhebung personenbezogener Daten?</h3>
        <p>
          Die Schulgesellschaft Sankt Martinus benötigt Ihre persönlichen Daten zur Bearbeitung Ihres Schulvertrages,
          Ihrer Bewerbung, Ihrer Verträge. Die Daten, die Sie uns übermitteln, werden für den Zweck des Schulbetriebes
          und/oder der Arbeitsvertragserstellung und ggf. späteren Abwicklung Ihres Arbeitsvertrages verwendet und
          verarbeitet. Zur Bearbeitung verwenden wir nur die Informationen, die Sie uns direkt übermittelt haben. Hierzu
          können auch Informationen gehören, die Sie schriftlich, elektronisch oder anderen Wegen angegeben haben.
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <h3>2. Welche personenbezogenen Daten erfassen wir?</h3>
        <p>
          Wir erfassen Ihre persönlichen Daten, die für die Vertragserstellung relevant sind oder die Sie uns im Zuge
          der Abwicklung des Vertrages übermitteln (darunter zum Beispiel Nachname, Vorname, Anschrift, E-Mail, für
          Bewerber zusätzlich die Position, auf die Sie sich bewerben, und Angaben zu Ihrer persönlichen Bewerbung). Um
          Ihre Eingruppierung bei Bewerbungen auf eine Stelle in den Kollegien sorgfältig zu beurteilen, benötigen wir
          auch zusätzliche Angaben, z.B. zu Ihrem bisherigen beruflichen Werdegang. <br />
          Gegebenenfalls fragen wir zusätzlich nach Ihren Vorlieben (z. B. im Hinblick auf Standorte und Jobgruppen, die
          für Sie von Interesse sind), um Sie für andere geeignete Aufgaben in Betracht ziehen zu können. <br />
          Liegt in unserem System mehr als ein Profil von Ihnen mit identischer E-Mailadresse vor, dann werden die Daten
          automatisch zusammengeführt. Alte Daten werden durch die Neueren überschrieben. Alle zur Verfügung gestellten/
          hochgeladenen Anhänge bleiben erhalten. <br />
          Im Fall einer Online-Bewerbung tragen Sie Ihre persönlichen Daten in die Online-Formulare ein und laden die
          zugehörigen Unterlagen hoch. Bei Bewerbungen über andere Kanäle erfassen wir die relevanten Informationen aus
          Ihrer Bewerbung und speichern Ihre Bewerbungsunterlagen in unserem System. <br />
          <br />
          Die Angabe Ihres Alters bzw. die Eingabe Ihres Geburtsdatums ist dadurch begründet, dass für einige unserer
          Tätigkeiten gesetzlich ein Mindestalter vorgesehen ist. Bei einer Staatsangehörigkeit außerhalb der EU wird
          Bewerbungen auf eine Stelle in unseren Kollegien eine Arbeitserlaubnis für Deutschland benötigt. Daher fragen
          wir Sie im Bewerbungsverfahren auch nach Ihrer Nationalität.
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <h3>3. Wer verarbeitet die Informationen?</h3>
        <p>
          Aufgrund der Organisationsstruktur der Schulgesellschaft Sankt Martinus und je nach dem von Ihnen gewählten
          Kanal können Ihre Daten von verschiedenen Stellen verarbeitet werden. In jedem Fall werden Ihre persönlichen
          Daten ausschließlich von uns und gegebenenfalls von DienstleisterInnen verarbeitet, die vertraglich gebunden
          und rechtlich verpflichtet sind, die einschlägigen Datenschutzbestimmungen einzuhalten. <br />
          Als BewerberIn können Sie bei Ihrer Onlinebewerbung auf eine konkrete Stelle oder auf die Teilnahme an einer
          Recruiting-Veranstaltung der Weitergabe Ihrer persönlichen Daten für andere in Betracht kommende Stellen aktiv
          zustimmen. Stimmen Sie nicht zu oder widerrufen Sie diese Zustimmung, hat dies zur Folge, dass keine weitere
          Berücksichtigung der Bewerbung für andere Stellen erfolgen kann.
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <h3>4. Wer wird über Ihre Bewerbung informiert?</h3>
        <p>
          Ihre persönlichen Daten werden streng vertraulich behandelt und nur den zuständigen Personen und
          AuftragnehmerInnen, die für das jeweilige Verfahren notwendige Leistungen erbringen (z. B. Landessysteme,
          Anbieter:in von Online-Tests, etc.), zur Verfügung gestellt. Details zu AuftragnehmerInnen finden Sie in Teil
          B. <br />
          Falls Sie für eine Beschäftigung in Betracht kommen, ist die nachfolgende Verwendung bzw. Weitergabe Ihrer
          persönlichen Daten auf rein beschäftigungsbezogene Zwecke und auf die Personengruppe beschränkt, die im Rahmen
          der Einstellung Kenntnis darüber erlangen muss. Dies gilt auch für Informationen, die internen oder externen
          DienstleisterInnen für erforderliche Vorbeschäftigungsprüfungen und aufgrund von rechtlichen oder behördlichen
          Anforderungen offengelegt werden. <br />
          Insbesondere ist es nach dieser Datenschutzrichtlinie unzulässig, Ihre Bewerbung bzw. Ihr Profil für andere
          Zwecke zu verwenden.
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <h3>5. Wie können Sie Ihre persönlichen Daten ändern?</h3>
        <p>
          Sie können Ihr Profil und Ihre personenbezogenen Informationen, einschließlich Ihrer persönlichen Daten und
          angehängten Dokumente, im Portal jederzeit öffnen und aktualisieren. <br />
          Wenn Sie möchten, dass wir Ihr Profil mit sofortiger Wirkung löschen, finden Sie in Teil B die konkrete
          E-Mail-Adresse, bei der Sie die Löschung verlangen können. In diesem Fall informieren wir auch etwaige
          AuftragnehmerInnen (z. B. AnbieterInnen von Online-Tests), die Ihre persönlichen Daten erhalten haben, und
          fordern sie zur sofortigen Löschung Ihrer persönlichen Daten auf. Ausgenommen sind Daten, die bereits für die
          Vertragsumsetzung benutzt wurden, hier greifen ggf. gesetzliche Aufbewahrungsfristen.
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <h3>6. Wann werden Ihre persönlichen Daten gelöscht?</h3>
        <p>
          Die jeweiligen Löschfristen auf Basis der unterschiedlichen lokalen Datenschutzgesetze sowie
          Aufbewahrungsfristen sind sehr vielfältig, daher ist eine Antwort nur individualisiert möglich. Wir speichern
          Ihre persönlichen Daten nur so lange wie notwendig bzw. gesetzlich erforderlich.
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <h3>7. Sie haben Fragen zum Datenschutz?</h3>
        <p>
          Für Fragen und Anregungen rund um das Thema Datenschutz wenden Sie sich bitte an unsere in Teil B genannten
          Ansprechperson.
        </p>
      </div>
    </div>
  </div>
`;

const TeilB = () => html`
  <div class="card">
    <div class="card-body space-y-4 prose prose-neutral max-w-none">
      <h2>Teil B – Zusätzliche Informationen</h2>

      <div class="space-y-2">
        <h3>1. Wer ist verantwortlich für die Datenerhebung und -verarbeitung?</h3>
        <p>
          Die Schulgesellschaft Sankt Martinus gGmbH, Abteilung Personal, Bischofsplatz 2, 55116 Mainz, erhebt und
          verarbeitet Ihre Daten als Verantwortlicher. <br />
          Die bestellte Datenschutzbeauftragte ist Frau Marion Minten:
          <a class="link link-primary" href="mailto:datenschutz@martinus.sg">datenschutz@martinus.sg</a> <br />
          Sollten Sie Fragen oder Anregungen zum Datenschutz im Zusammenhang mit der Personalgewinnung bei uns haben,
          kontaktieren Sie uns bitte per E-Mail oder Post.
        </p>
      </div>

      <div class="space-y-2">
        <h3>2. Auf welcher Rechtsgrundlage und wie erheben wir Ihre persönlichen Daten?</h3>
        <p>
          Wir erheben Ihre persönlichen Daten zum Zweck der Durchführung der Verfahren auf der Rechtsgrundlage des Art.
          6 Abs. 1 a), b) und f), Art. 88 Datenschutzgrundverordnung (DSGVO) i.V.m. § 26 Abs. 1 Satz 1, 1. Alt.
          Bundesdatenschutzgesetz (BDSG). Mit einem eigenen Zugang geben Sie Ihre Daten selbständig in das entsprechende
          Onlineformular ein. Bei einer zu erfassenden E-Mail erfassen wir alle relevanten Daten in unserem System und
          laden Ihre Dokumente hoch. Bei einer Papiervorlage erfassen wir ebenfalls Ihre vorgelegten Daten und scannen,
          falls erforderlich, ggf. Ihren Schriftverkehr ein und laden diese in unserem System hoch.
          <br />
          Rechtsgrundlagen für die Überprüfung der persönlichen Eignung sind § 26 Abs. 1 S. 1 BDSG, Art. 6 Abs. 1 f)
          DSGVO. Unser berechtigtes Interesse besteht in der Integritätspflicht der Beschäftigten in der Aufsicht,
          Betreuung, Erziehung oder Ausbildung Minderjähriger.
        </p>
      </div>

      <div class="space-y-2">
        <h3>3. Betroffenenrechte</h3>
        <p>
          Sie haben das Recht, jederzeit Auskunft zu den über Sie gespeicherten personenbezogenen Daten zu verlangen.
          Ferner haben Sie das Recht, die Berichtigung Sie betreffender unrichtiger personenbezogener Daten zu
          verlangen, einschließlich der Vervollständigung unvollständiger Daten, ggf. in Form einer ergänzenden
          Erklärung. Zudem haben Sie ggf. das Recht, die Löschung der zu Ihnen gespeicherten personenbezogenen Daten zu
          verlangen. Sollten gesetzliche Vorschriften, z.B. Aufbewahrungspflichten, eine Löschung nicht zulassen, werden
          Ihre Daten stattdessen in der Verarbeitung beschränkt (d. h. gesperrt), so dass sie nur noch zum Zweck der
          Beachtung der zwingenden gesetzlichen Vorschriften zugänglich sind. Ferner haben Sie ggf. das Recht, eine
          Einschränkung der Verarbeitung (Sperrung) Ihrer Daten zu verlangen. In Bezug auf personenbezogene Daten, die
          Sie uns bereitgestellt haben und die von uns mithilfe automatisierter Verfahren aufgrund einer Einwilligung
          oder eines Vertrags verarbeitet werden, haben Sie das Recht, die Übertragung dieser Daten an Sie oder ggf. an
          einen von Ihnen benannten Dritten zu verlangen. Sie können der Datenverarbeitung aus Gründen, die sich aus
          Ihrer besonderen Situation ergeben, widersprechen, wenn die Datenverarbeitung aufgrund unserer überwiegenden
          berechtigten Interesse erfolgt oder für die Wahrnehmung einer öffentlichen Aufgabe erforderlich ist. Sofern
          die Datenverarbeitung auf Ihrer Einwilligung beruht, haben Sie das Recht, die Einwilligung jederzeit zu
          widerrufen. Sie haben zudem das Recht auf Beschwerde bei der Aufsichtsbehörde.
        </p>
      </div>
    </div>
  </div>
`;

const TeilC = () => html`
  <div class="card">
    <div class="card-body space-y-4 prose prose-neutral max-w-none">
      <h2>Teil C – Technische Information</h2>
      <p>
        Aus technischen Gründen müssen beim Besuch von dieser Webseite bestimmte Daten erhoben und gespeichert werden.
      </p>

      <div class="space-y-2">
        <h3>1. Wie werden Ihre persönlichen Daten übermittelt?</h3>
        <p>
          Die Internet-Server unseres Providers erfüllen aktuelle Sicherheitsstandards. Wir versuchen damit, Ihre
          persönlichen Daten gegen Verlust, Missbrauch, unbefugten Zugriff, Bekanntwerden, Änderung und Löschung zu
          schützen. Die Übermittlung Ihrer persönlichen Daten an die Server erfolgt nach den Sicherheitsstandards der
          SSL- oder TLS-Methode.
        </p>
      </div>

      <div class="space-y-2">
        <h3>2. Was ist ein Cookie?</h3>
        <div class="space-y-1">
          <h4>2.1. Was ist ein Cookie?</h4>
          <p>
            Cookies sind kleine Textdateien, in denen personenbezogene Daten gespeichert werden können. Die Cookies
            können beim Aufruf einer Seite an diese übermittelt werden und ermöglichen somit eine Zuordnung des Nutzers.
            Cookies helfen dabei, die Nutzung von Internetseiten für die Nutzer zu vereinfachen. <br />
            Wir unterscheiden zwischen Cookies, die für die technischen Funktionen der Webseite zwingend erforderlich
            sind und solchen Cookies, die für die technische Funktion der Webseite nicht zwingend erforderlich sind. Wir
            möchten Ihnen die Möglichkeit zu einer informierten Entscheidung für oder gegen den Einsatz von Cookies
            geben, die für die technischen Funktionen der Webseite nicht zwingend erforderlich sind. Die Nutzung der
            Webseite bleibt aber in vollem Umfang weiterhin möglich.
          </p>
        </div>

        <div class="space-y-1">
          <h4>2.2. Wie werden Cookies verwendet?</h4>
          <p>
            Die Nutzung des Systems ist generell ohne Cookies, die nicht technischen Zwecken dienen, möglich. Sie können
            also in Ihrem Browser die Nachverfolgbarkeit durch Cookies verhindern (Do-not-track,
            Tracking-Protection-List) bzw. das Speichern von Drittanbietercookies untersagen. Außerdem empfehlen wir
            eine regelmäßige Kontrolle der gespeicherten Cookies vorzunehmen, sofern diese nicht ausdrücklich gewünscht
            sind. <br />
            Bitte beachten Sie: Bei Löschung aller Cookies werden auch etwaige gesetzte Widerspruchscookies
            (Opt-Out-Cookies) gelöscht, sodass Sie etwaige erklärte Widersprüche erneut ausüben müssten. <br />
            Cookies, die zwingend für die Nutzung der Seite erforderlich sind: <br />
            Session Cookies können nicht deaktiviert werden, da sie für die Nutzung dieser Website benötigt werden.
            <br />
            Der „sessionID Cookie“ wird von der Plattform in zustandslosen Protokollen als Identifizierungskriterien
            verwendet, um mehrere zusammengehörige Anfragen eines Clients zu erkennen und sie ein und derselben Sitzung
            zuzuordnen. Dieses Cookie enthält eine lange alphanumerische Kennung, die ebenfalls auf dem Server
            gespeichert wird. Wenn beide IDs übereinstimmen, darf der Client eine Anfrage an den Server senden. Die
            Sitzung und der Cookie werden beim Schließen der Seite vernichtet. <br />
          </p>
        </div>
      </div>
    </div>
  </div>
`;

export const Datenschutz = stateful(
  () => html`
    <div class="flex flex-col bg-base-200 w-full items-center overflow-y-auto ">
      ${HeaderBar()}
      <div class="flex flex-col container gap-8 sm:mt-16 mt-8">
        ${Intro()} ${TeilA()} ${TeilB()} ${TeilC()}
        <div class="text-sm opacity-70 text-center prose prose-neutral max-w-none mb-8">
          <p>
            <strong>**</strong> Aus Gründen der besseren Lesbarkeit wird auf die gleichzeitige Verwendung verschiedener
            geschlechtlicher Formulierungen verzichtet. Alle Beschreibungen gelten jedoch für jedes Geschlecht.
          </p>
        </div>
      </div>
    </div>
  `,
);
